/**
 * LLM Metrics page — provider performance, failure rates, response times.
 *
 * Header-only migration onto the CoOrdio shell chrome: `ShellHeader` + `PageContainer` replace the
 * hand-rolled MUI `Box` + `Typography` header, so the page's title bar now matches every migrated
 * list page. The chart body (`LlmMetricsCharts`) still owns its own MUI internals — a full
 * chart-body migration is a separate PR (chart tokens live under `useChartTokens.ts`).
 *
 * The time-range switch is rebuilt as a plain `Button` group inside the header actions slot;
 * `ToggleButtonGroup` was the last MUI-specific control on the outer chrome.
 */

import { useState, useEffect } from 'react';

import { Button, ShellHeader } from '@coordio/ui';
import { EmptyState, InlineErrorPanel, PageContainer } from '@coordio/ui/layout';

import { LlmMetricsCharts } from '../components/dashboard/LlmMetricsCharts';
import { dashboardService } from '../services/dashboardService';
import { useContextStore } from '../store/contextStore';
import type { LlmCallMetricsDto } from '../types/llmMetrics';

import './LlmMetricsPage.scss';

const RANGE_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
] as const;

export const LlmMetricsPage = () => {
  const { currentTenant, currentProject } = useContextStore();
  const [data, setData] = useState<LlmCallMetricsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<number>(7);

  useEffect(() => {
    if (!currentTenant?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    dashboardService
      .getLlmMetrics(currentTenant.id, currentProject?.id || undefined, days)
      .then(setData)
      .catch((err) => setError(err.response?.data?.message || err.message || 'Failed to load LLM metrics'))
      .finally(() => setLoading(false));
  }, [currentTenant?.id, currentProject?.id, days]);

  const rangeToggle = (
    <div className="llm-metrics__range" role="group" aria-label="Time range">
      {RANGE_OPTIONS.map((option) => (
        <Button
          key={option.value}
          type="button"
          variant={days === option.value ? 'default' : 'outline'}
          size="sm"
          onClick={() => setDays(option.value)}
          aria-pressed={days === option.value}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );

  if (!currentTenant) {
    return (
      <div className="llm-metrics" data-testid="llm-metrics-detail-page">
        <ShellHeader
          title="LLM Metrics"
          description="Provider performance, failure rates, and response times."
        />
        <PageContainer>
          <EmptyState
            title="No workspace selected"
            description="Choose a workspace from the user menu to see its LLM metrics."
          />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="llm-metrics" data-testid="llm-metrics-detail-page">
      <ShellHeader
        title="LLM Metrics"
        description="Provider performance, failure rates, and response times."
        actions={rangeToggle}
      />

      <PageContainer>
        {error && (
          <div className="llm-metrics__error">
            <InlineErrorPanel
              message={error}
              action={
                <Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>
                  Dismiss
                </Button>
              }
              data-testid="llm-metrics-detail-error"
            />
          </div>
        )}

        <LlmMetricsCharts data={data} loading={loading} />
      </PageContainer>
    </div>
  );
};
